// Logique pure du kanban « Mes tâches » (LOT 1).
// Fonctions sans effet de bord, testées en isolation (Jest). Le composant
// MesTachesKanban délègue ici tout ce qui peut l'être (groupBy, validation,
// déduction prop_id, résolution des libellés).

import { getAllLogsFlat } from '../../lib/sousloc-helpers'

// ── Constantes métier ───────────────────────────────────────────────
export const STATUSES = ['todo', 'doing', 'done']

export const STATUS_LABELS = {
  todo: 'À faire',
  doing: 'En cours',
  done: 'Fait',
}

export const SCOPES = ['logement', 'proprio', 'entreprise', 'divers']

export const SCOPE_LABELS = {
  logement: 'Logement',
  proprio: 'Proprio',
  entreprise: 'Entreprise',
  divers: 'Divers',
}

export const PRIORITIES = ['basse', 'normale', 'haute']

export const PRIORITY_LABELS = {
  basse: 'Basse',
  normale: 'Normale',
  haute: 'Haute',
}

// Couleurs de pastille priorité (charte DS interne admin).
export const PRIORITY_COLORS = {
  basse: '#8fa3bc',   // textLight (neutre)
  normale: '#3d5166', // charte 2026
  haute: '#e11d48',   // charge (rouge fort)
}

// ── Whitelist statut ────────────────────────────────────────────────
// Garde anti-écriture : on n'écrit JAMAIS un status hors whitelist en base
// (un drag sur une zone inattendue, un id de colonne corrompu, etc.).
export function isValidStatus(status) {
  return STATUSES.includes(status)
}

// ── Colonnes spéciales (scope sans logement) ────────────────────────
// Vues « Par logement » et « Swimlanes » : les tâches sans logement
// tombent dans une colonne/ligne spéciale selon leur scope. On ne
// matérialise QUE les scopes sans logement (entreprise / proprio / divers).
// Le scope 'logement' n'a pas de colonne spéciale — ces tâches vont dans
// la colonne de leur logement_id.
export const SPECIAL_SCOPES = ['entreprise', 'proprio', 'divers']

export const SPECIAL_SCOPE_LABELS = {
  entreprise: '🏢 Entreprise FBZ',
  proprio: '👤 Proprio',
  divers: '📌 Divers',
}

// Clé de ligne/colonne d'une tâche pour les vues logement & swimlane.
// - tâche de scope 'logement' avec logement_id → ce logement_id ;
// - sinon → une clé spéciale `special:<scope>` (entreprise/proprio/divers).
// Une tâche scope=logement SANS logement_id retombe en 'divers' (sécurité :
// jamais de colonne fantôme sans nom résolu).
export function rowKeyForTask(task) {
  if (task.scope === 'logement' && task.logement_id) return task.logement_id
  const scope = SPECIAL_SCOPES.includes(task.scope) ? task.scope : 'divers'
  return specialKey(scope)
}

export function specialKey(scope) {
  return `special:${scope}`
}

// Décode une clé de colonne spéciale → scope, ou null si ce n'est pas une
// colonne spéciale (= c'est un logement_id).
export function parseSpecialKey(key) {
  if (typeof key === 'string' && key.startsWith('special:')) {
    const scope = key.slice('special:'.length)
    return SPECIAL_SCOPES.includes(scope) ? scope : null
  }
  return null
}

// ── Échéance : buckets agenda (Europe/Paris) ────────────────────────
// 4 colonnes : today / week / later / none.
//   - none   : due_at null ;
//   - today  : même jour calendaire (Europe/Paris) que maintenant ;
//   - week   : entre demain et la fin de la semaine courante (dimanche inclus) ;
//   - later  : au-delà de dimanche ;
//   - dépassée (avant aujourd'hui) → today (reste urgent/rouge, à traiter).
export const DUE_BUCKETS = ['today', 'week', 'later', 'none']

export const DUE_BUCKET_LABELS = {
  today: "Aujourd'hui",
  week: 'Cette semaine',
  later: 'Plus tard',
  none: 'Sans échéance',
}

// Renvoie {y,m,d} en Europe/Paris pour une Date donnée (parts calendaires
// locales Paris, indépendant du fuseau du runtime — important en CI UTC).
function parisParts(date) {
  const fmt = new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Europe/Paris',
    year: 'numeric', month: '2-digit', day: '2-digit', weekday: 'short',
  })
  const parts = {}
  for (const p of fmt.formatToParts(date)) parts[p.type] = p.value
  return {
    y: Number(parts.year),
    m: Number(parts.month),
    d: Number(parts.day),
  }
}

// Index ordinal d'un jour (proxy comparable) à partir des parts Paris.
function dayOrdinal({ y, m, d }) {
  return y * 10000 + m * 100 + d
}

// Jour de la semaine ISO en Europe/Paris : lundi=1 … dimanche=7.
function parisIsoWeekday(date) {
  const wd = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Europe/Paris', weekday: 'short',
  }).format(date)
  const map = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 7 }
  return map[wd] || 1
}

// Bucket d'échéance d'une tâche. now injectable pour les tests.
export function dueBucket(dueAt, now = new Date()) {
  if (!dueAt) return 'none'
  const due = dueAt instanceof Date ? dueAt : new Date(dueAt)
  if (Number.isNaN(due.getTime())) return 'none'

  const todayOrd = dayOrdinal(parisParts(now))
  const dueOrd = dayOrdinal(parisParts(due))

  if (dueOrd <= todayOrd) return 'today' // aujourd'hui OU dépassée → à traiter
  // Fin de semaine courante (dimanche) en ordinal Paris.
  const isoWd = parisIsoWeekday(now)
  const sunday = new Date(now.getTime() + (7 - isoWd) * 24 * 60 * 60 * 1000)
  const sundayOrd = dayOrdinal(parisParts(sunday))
  if (dueOrd <= sundayOrd) return 'week'
  return 'later'
}

// ── groupBy : 4 vues commutables (Lot 2) ────────────────────────────
// Retourne un objet { cleA: [tasks...], ... } selon la vue demandée.
//   - 'status'   : 3 clés todo/doing/done (vue Statuts, Lot 1 — INCHANGÉE) ;
//   - 'logement' : 1 clé par logement_id + clés spéciales `special:<scope>` ;
//   - 'swimlane' : 1 clé par cellule `cell:<rowKey>:<status>` (grille
//                  lignes=logements/scopes × colonnes=statuts) ;
//   - 'echeance' : 4 clés today/week/later/none (buckets Europe/Paris).
// `now` injectable pour rendre la vue échéance testable.
export function groupBy(tasks, view = 'status', { now = new Date() } = {}) {
  const list = Array.isArray(tasks) ? tasks : []
  if (view === 'status') {
    const groups = { todo: [], doing: [], done: [] }
    for (const t of list) {
      const key = isValidStatus(t.status) ? t.status : 'todo'
      groups[key].push(t)
    }
    for (const k of STATUSES) groups[k].sort(byPosition)
    return groups
  }
  if (view === 'logement') {
    const groups = {}
    for (const t of list) {
      const key = rowKeyForTask(t)
      ;(groups[key] = groups[key] || []).push(t)
    }
    for (const k of Object.keys(groups)) groups[k].sort(byPosition)
    return groups
  }
  if (view === 'swimlane') {
    // Grille : 1 clé par cellule (ligne logement/scope × colonne statut).
    const groups = {}
    for (const t of list) {
      const row = rowKeyForTask(t)
      const status = isValidStatus(t.status) ? t.status : 'todo'
      const key = cellKey(row, status)
      ;(groups[key] = groups[key] || []).push(t)
    }
    for (const k of Object.keys(groups)) groups[k].sort(byPosition)
    return groups
  }
  if (view === 'echeance') {
    const groups = { today: [], week: [], later: [], none: [] }
    for (const t of list) {
      groups[dueBucket(t.due_at, now)].push(t)
    }
    for (const k of DUE_BUCKETS) groups[k].sort(byPosition)
    return groups
  }
  return { all: [...list].sort(byPosition) }
}

// Clé d'une cellule swimlane (ligne × colonne statut).
export function cellKey(rowKey, status) {
  return `cell:${rowKey}:${status}`
}

// Décode une clé de cellule swimlane `cell:<rowKey>:<status>`.
// rowKey peut contenir ':' (cas `special:divers`) → on découpe sur le DERNIER
// segment pour isoler le statut, le reste = rowKey.
export function parseCellKey(key) {
  if (typeof key !== 'string' || !key.startsWith('cell:')) return null
  const body = key.slice('cell:'.length)
  const lastColon = body.lastIndexOf(':')
  if (lastColon < 0) return null
  const status = body.slice(lastColon + 1)
  const rowKey = body.slice(0, lastColon)
  if (!isValidStatus(status)) return null
  return { rowKey, status }
}

function byPosition(a, b) {
  return (a.position ?? 0) - (b.position ?? 0)
}

// ── Lignes/colonnes ordonnées des vues logement & swimlane ──────────
// Construit l'ordre des lignes : d'abord les logements (NOM résolu via
// getAllLogsFlat, dédup byId), puis les 3 colonnes spéciales. Chaque entrée
// { key, label, kind:'logement'|'special', logementId?, scope? }.
export function buildRows(cfg) {
  const rows = []
  for (const l of getAllLogsFlat(cfg)) {
    rows.push({ key: l.id, label: l.nom || l.id, kind: 'logement', logementId: l.id })
  }
  for (const s of SPECIAL_SCOPES) {
    rows.push({ key: specialKey(s), label: SPECIAL_SCOPE_LABELS[s], kind: 'special', scope: s })
  }
  return rows
}

// ── Résolveurs de patch par vue (drag → champ à écrire) ─────────────
// Toute la sémantique du drag est centralisée ici (pur, testable). Le
// composant n'a qu'à appliquer le patch retourné (optimistic + supabase).
// Retourne `null` si le drop est un no-op ou invalide (→ pas d'écriture).

// VUE PAR LOGEMENT : drop sur la colonne `overKey`.
//   - colonne logement (logement_id) → scope='logement', logement_id=cible,
//     prop_id déduit via cfg ;
//   - colonne spéciale `special:<scope>` → scope=<scope>, logement_id=null,
//     prop_id=null (on nettoie le rattachement logement).
export function patchForLogementDrop(task, overKey, cfg) {
  if (!task || !overKey) return null
  const scope = parseSpecialKey(overKey)
  if (scope) {
    if (task.scope === scope && !task.logement_id) return null
    return { scope, logement_id: null, prop_id: null }
  }
  // Sinon overKey = un logement_id.
  if (task.scope === 'logement' && task.logement_id === overKey) return null
  return {
    scope: 'logement',
    logement_id: overKey,
    prop_id: deduceProporId(cfg, overKey),
  }
}

// VUE SWIMLANES : drop sur la cellule `overKey` = cell:<rowKey>:<status>.
//   - même ligne, autre colonne → change STATUT ;
//   - autre ligne, même colonne → change LOGEMENT/scope ;
//   - les deux peuvent changer en même temps (drag diagonal) → on patche
//     statut ET logement/scope.
// Le done_at est géré par le composant (cohérent avec la vue Statuts).
export function patchForSwimlaneDrop(task, overKey, cfg) {
  if (!task) return null
  const cell = parseCellKey(overKey)
  if (!cell) return null
  const patch = {}
  // Composante statut.
  if (task.status !== cell.status) patch.status = cell.status
  // Composante ligne (logement/scope).
  const currentRow = rowKeyForTask(task)
  if (currentRow !== cell.rowKey) {
    const rowPatch = patchForLogementDrop(task, cell.rowKey, cfg)
    if (rowPatch) Object.assign(patch, rowPatch)
  }
  return Object.keys(patch).length ? patch : null
}

// VUE PAR ÉCHÉANCE : drop sur le bucket `overKey` (today/week/later/none).
//   - 'today' → due_at = aujourd'hui (minuit Paris), due_all_day=true ;
//   - 'none'  → due_at = null ;
//   - 'week'  → due_at = dimanche fin de semaine courante SI la tâche n'est
//               pas déjà dans la semaine (sinon no-op) ;
//   - 'later' → due_at = aujourd'hui +14j (date représentative).
// `now` injectable pour les tests.
export function patchForEcheanceDrop(task, overKey, now = new Date()) {
  if (!task || !DUE_BUCKETS.includes(overKey)) return null
  const currentBucket = dueBucket(task.due_at, now)
  if (currentBucket === overKey) return null // déjà dans ce bucket → no-op
  if (overKey === 'none') {
    return { due_at: null, due_all_day: true }
  }
  if (overKey === 'today') {
    return { due_at: parisMidnightISO(now), due_all_day: true }
  }
  if (overKey === 'week') {
    const isoWd = parisIsoWeekday(now)
    const sunday = new Date(now.getTime() + (7 - isoWd) * 24 * 60 * 60 * 1000)
    return { due_at: parisMidnightISO(sunday), due_all_day: true }
  }
  // 'later' → +14 jours (représentatif, l'admin précisera dans la modale).
  const later = new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000)
  return { due_at: parisMidnightISO(later), due_all_day: true }
}

// Renvoie l'ISO du minuit (00:00) Europe/Paris du jour calendaire de `date`.
// On calcule l'offset Paris du jour (gère l'heure d'été CEST/CET) pour poser
// un instant qui correspond bien à minuit local Paris.
function parisMidnightISO(date) {
  const { y, m, d } = parisParts(date)
  // Offset Paris du jour, en minutes (ex. +120 en été, +60 en hiver).
  const offsetMin = parisOffsetMinutes(date)
  // minuit Paris en UTC = minuit local − offset.
  const utcMs = Date.UTC(y, m - 1, d, 0, 0, 0) - offsetMin * 60 * 1000
  return new Date(utcMs).toISOString()
}

// Offset (minutes) d'Europe/Paris pour l'instant `date` (positif à l'est de
// UTC). Déduit en comparant l'heure formatée Paris à l'heure UTC.
function parisOffsetMinutes(date) {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Europe/Paris', hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  })
  const parts = {}
  for (const p of fmt.formatToParts(date)) parts[p.type] = p.value
  const asUTC = Date.UTC(
    Number(parts.year), Number(parts.month) - 1, Number(parts.day),
    Number(parts.hour === '24' ? '00' : parts.hour), Number(parts.minute), Number(parts.second),
  )
  return Math.round((asUTC - date.getTime()) / 60000)
}

// ── Déduction prop_id depuis logement_id via cfg ────────────────────
// On stocke prop_id à la création pour éviter un lookup permanent et pour
// garder une trace immuable. Résolu via getAllLogsFlat (dédup byId, jamais
// par nom). Retourne null si introuvable / pas de logement_id.
export function deduceProporId(cfg, logementId) {
  if (!logementId) return null
  const log = getAllLogsFlat(cfg).find(l => l.id === logementId)
  return log ? (log.propId || null) : null
}

// ── Résolution des NOMS (jamais d'id brut à l'écran) ────────────────
export function resolveLogementNom(cfg, logementId) {
  if (!logementId) return null
  const log = getAllLogsFlat(cfg).find(l => l.id === logementId)
  return log ? (log.nom || logementId) : logementId
}

export function resolveProprioNom(cfg, propId) {
  if (!propId) return null
  const p = (cfg?.props || []).find(x => x.id === propId)
  return p ? (p.nom || propId) : propId
}

// ── Échéance : dépassée / aujourd'hui (→ affichage rouge) ───────────
// dueAt dépassée si < maintenant ; "aujourd'hui" si même jour calendaire.
// On considère le rouge pour due dépassée OU due aujourd'hui (rappel urgent).
export function isDueUrgent(dueAt, now = new Date()) {
  if (!dueAt) return false
  const due = dueAt instanceof Date ? dueAt : new Date(dueAt)
  if (Number.isNaN(due.getTime())) return false
  if (due.getTime() < now.getTime()) return true
  return isSameDay(due, now)
}

function isSameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

// ── Formatage échéance pour affichage ───────────────────────────────
export function formatDue(dueAt, allDay) {
  if (!dueAt) return null
  const d = dueAt instanceof Date ? dueAt : new Date(dueAt)
  if (Number.isNaN(d.getTime())) return null
  const date = d.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
  if (allDay) return date
  const time = d.toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  })
  return `${date} · ${time}`
}

// ── Filtre actif (scope / logement / étiquette) ─────────────────────
// Applique les filtres + exclut les archivés (sauf vue archives explicite).
export function applyFilters(tasks, { scope, logementId, labelId, archived = false } = {}) {
  const list = Array.isArray(tasks) ? tasks : []
  return list.filter(t => {
    const isArchived = !!t.archived_at
    if (archived !== isArchived) return false
    if (scope && t.scope !== scope) return false
    if (logementId && t.logement_id !== logementId) return false
    if (labelId && !(Array.isArray(t.labels) && t.labels.includes(labelId))) return false
    return true
  })
}

// ── Retrait d'un label de toutes les tâches (suppression d'étiquette) ─
// Pur : retourne la liste des tâches dont labels contient labelId, avec le
// nouveau tableau labels (sans labelId). Le composant persiste ensuite.
export function tasksAffectedByLabelRemoval(tasks, labelId) {
  const list = Array.isArray(tasks) ? tasks : []
  return list
    .filter(t => Array.isArray(t.labels) && t.labels.includes(labelId))
    .map(t => ({ id: t.id, labels: t.labels.filter(l => l !== labelId) }))
}
